import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Errors } from './Errors';
import { Dispatch, SetStateAction } from 'react';
import {
  getGTMProductData,
  pushDataLayer,
  trackSubmitForm,
} from '@/lib/utilities/gtm';
import CountrySelector from './CountrySelector';
import { PrivacyLabel } from '../Cookies/PrivacyLabel';
import { omitBy } from 'lodash-es';
import { Product } from '@/lib/types/bevmaq.types';
import { setErrorOn400 } from './lib/setErrorOn400';

interface IFormInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  companyName: string;
  comment: string;
  terms: boolean;
  manufacturer: string;
  model: string;
  type: string;
  sku: string;
  leadType: string;
  leadPlatform: string;
}

export function BuyForm({
  setIsSubmitted,
  product,
}: {
  setIsSubmitted: Dispatch<SetStateAction<boolean>>;
  product: Product;
}) {
  const { t } = useTranslation('common');
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    control,
    setError,
  } = useForm<IFormInput>({
    mode: 'all',
  });
  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    data.sku = product.sku;
    data.leadType = 'Quote Request';
    data.leadPlatform = 'BEVMAQ Website';
    console.log('submited', data);
    setIsSubmitted(true);
    fetch(process.env.NEXT_PUBLIC_CRM_API_URL + '/buylead', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(omitBy(data, (value) => value === '')),
    })
      .then(setErrorOn400(setError))
      //Then with the data from the response in JSON...
      .then((data) => {
        console.log('Success:', data);
        trackSubmitForm({
          action: 'Buy',
          category: 'Quote Request',
          label: product.sku as string,
        });
        pushDataLayer('begin_checkout', {
          ecommerce: {
            value: product.price?.amount,
            currency: product.price?.currency,
            items: [getGTMProductData(product)],
          },
        });
      })
      .catch((error) => {
        console.warn('error on POST /buy lead', error);
        setIsSubmitted(false);
      });
  };
  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className='space-y-4 rounded '
      data-testid='buy-form'
      noValidate
    >
      <div className='space-y-4 md:flex md:space-x-4 md:space-y-0'>
        <div className='flex-grow'>
          <label className='input-label' htmlFor='firstName'>
            {t('First Name')} *
          </label>
          <input
            className={classNames({
              'input-field': true,
              'border-red-500': errors.firstName?.message !== undefined,
            })}
            id={'firstName'}
            type='text'
            {...register('firstName', {
              required: t('common:Enter your name'),
            })}
          />
          <Errors data={errors.firstName} />
        </div>
        <div className='flex-grow'>
          <label className='input-label' htmlFor='lastName'>
            {t('Last Name')} *
          </label>
          <input
            className={classNames({
              'input-field': true,
              'border-red-500': errors.lastName?.message !== undefined,
            })}
            id={'lastName'}
            type='text'
            {...register('lastName', {
              required: t('Required'),
            })}
          />
          <Errors data={errors.lastName} />
        </div>
      </div>
      <div className='space-y-4 md:flex md:space-x-4 md:space-y-0'>
        <div className='flex-grow'>
          <label className='input-label' htmlFor='email'>
            {t('email')} *
          </label>
          <input
            className={classNames({
              'input-field': true,
              'border-red-500': errors.email?.message !== undefined,
            })}
            id={'email'}
            type='email'
            placeholder={'example@gmail.com'}
            {...register('email', {
              required: t('common:Required'),
              pattern: {
                value: /([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+/,
                message: t('common:Invalid email address'),
              },
            })}
          />
          <Errors data={errors.email} />
        </div>
        <div>
          <label className='input-label' htmlFor='phone'>
            {t('Phone')} *
          </label>
          <input
            className={classNames({
              'input-field': true,
              'border-red-500': errors.phone?.message !== undefined,
            })}
            id={'phone'}
            type='text'
            placeholder={t('sell.eg.phone')}
            {...register('phone', {
              required: t('Required'),
            })}
          />
          <Errors data={errors.phone} />
        </div>
        <CountrySelector
          error={errors.country}
          register={register}
          control={control}
          containerClasses='flex-grow'
        />
      </div>
      <div>
        <label className='input-label' htmlFor='companyName'>
          {t('Company Name')} *
        </label>
        <input
          className={classNames({
            'input-field': true,
            'border-red-500': errors.companyName?.message !== undefined,
          })}
          id={'companyName'}
          type='text'
          {...register('companyName', {
            required: t('Required'),
          })}
        />
        <Errors data={errors.companyName} />
      </div>
      <div>
        <label className='input-label' htmlFor='comment'>
          {t('Comment')}
        </label>
        <textarea
          className={classNames({
            'input-field': true,
            'border-red-500': errors.comment?.message !== undefined,
          })}
          id={'comment'}
          {...register('comment')}
        />
      </div>
      <div className='form-check mb-4'>
        <input
          className='float-left mr-2 mt-1 h-4 w-4 cursor-pointer appearance-none rounded-sm border border-gray-300 bg-white bg-contain bg-center bg-no-repeat align-top transition duration-200 checked:border-blue-600 checked:bg-blue-600 focus:outline-none'
          type='checkbox'
          id='terms'
          {...register('terms', {
            required: t('Required'),
          })}
        />
        <label className='text-gray-800' htmlFor='terms'>
          <PrivacyLabel />
        </label>
        <Errors data={errors.terms} />
      </div>
      <button
        type='submit'
        className={classNames({
          'h-16 w-full rounded px-4 py-2 uppercase text-white ': true,
          'bg-blue-500 hover:bg-blue-600': isValid,
          'bg-gray-500': !isValid,
        })}
      >
        <h3 className='text-xl'>{t('Send')}</h3>
      </button>
    </form>
  );
}
